import store from 'redux/store';
import { deleteWorker, enableWorker, disableWorker, restartWorker } from 'redux/slices/workersSlice';
import { confirmDialog } from 'dialogs';
import { addToTransportExecutor } from 'actions/factories/addToTransportExecutor';
import { deleteExecutor } from 'actions/factories/deleteExecutor';
import type { ActionDefinitions, ActionResult } from 'actions/Action';
import type { ActionEntity } from 'actions/ActionEntity';
import { showEditWorker } from './edit/showEditWorker';

type WorkerEntity = Extract<ActionEntity, { type: 'worker' }>;

const isAllWorker = (entities: readonly ActionEntity[]): entities is readonly WorkerEntity[] => entities.every((entity) => entity.type === 'worker');

/**
 * Action definitions for workers. `createWorker` / `addToTransportWorker` are
 * scoped; `edit` / `delete` / `enable` / `disable` / `restart` are contextual
 * and single. enable/disable/restart are state-gated: `'disabled'` when already
 * in that state (mirroring the old greyed menu items); restart confirms first.
 * These fire no explicit refresh beyond the channel — the slice reducers flip
 * enabled / maintenance in place.
 */
export const workerActionDefinitions: ActionDefinitions = [
  {
    id: 'createWorker',
    scope: 'type',
    label: 'New worker',
    resolve: () => () => showEditWorker({ mode: 'create' }),
  },
  {
    id: 'edit',
    label: 'Edit',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (!isAllWorker(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      return () => showEditWorker({ mode: 'edit', id: only.id });
    },
  },
  {
    id: 'delete',
    label: 'Delete',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllWorker(entities)) return 'hidden';
      if (entities.length === 0) return 'disabled';
      return deleteExecutor(entities, (entity) => store.dispatch(deleteWorker(entity.name)), {
        refresh,
        confirmBody: 'Are you sure you want to delete this worker?',
      });
    },
  },
  {
    id: 'enable',
    label: 'Enable',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllWorker(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      if (only.enabled) return 'disabled';
      return () => {
        void store.dispatch(enableWorker(only.name));
        refresh();
      };
    },
  },
  {
    id: 'disable',
    label: 'Disable',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllWorker(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      if (!only.enabled) return 'disabled';
      return () => {
        void store.dispatch(disableWorker(only.name));
        refresh();
      };
    },
  },
  {
    id: 'restart',
    label: 'Restart',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllWorker(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      if (only.maintenanceModeActive) return 'disabled';
      return () =>
        void confirmDialog({
          title: 'Confirm Restart',
          body: 'Are you sure you want to restart this worker? It will disconnect briefly and reconnect automatically.',
          confirmText: 'Restart',
          confirmVariant: 'warning',
        }).then((ok) => {
          if (!ok) return;
          void store.dispatch(restartWorker(only.name));
          refresh();
        });
    },
  },
  {
    id: 'addToTransportWorker',
    label: 'Add to Transport',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (entities.length === 0) return 'disabled';
      if (!isAllWorker(entities)) return 'hidden';
      const single = entities.length === 1 ? entities[0] : null;
      return addToTransportExecutor(
        'worker',
        entities.map((entity) => entity.id),
        { objectLabel: single ? single.name : 'Selected workers' }
      );
    },
  },
];
